import React from "react";
import { observer } from "mobx-react-lite";
import { Box, IconButton, Typography, Button, CircularProgress } from "@material-ui/core";
import SystemUpdateTwoToneIcon from "@material-ui/icons/SystemUpdateTwoTone";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { Uploader } from "Components/Uploader/Uploader";
import { ProgrammeUpdatesDocumentsViewModel } from "./ProgrammeUpdatesDocumentsViewModel";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { Attachment } from "../ProgrammeUpdatesView";
import { isEvidenceAnImage } from "Utils/Utils";
import { ProgrammeUpdatesViewModel } from "../ProgrammeUpdatesViewModel";
import { ProgrammeUpdatesDocumentsModel } from "./ProgrammeUpdatesDocumentsModel";

interface Props {
    viewModel: ProgrammeUpdatesDocumentsViewModel;
    parentViewModel: ProgrammeUpdatesViewModel;
    isFormDisabled: boolean;
    isLoading: boolean;
    openEvidenceModal: (attachment: Attachment) => void;
}

export const ProgrammeUpdatesDocumentsView: React.FC<Props> = observer(({ viewModel, isFormDisabled, isLoading, openEvidenceModal, parentViewModel }) => {
    const renderFileName = (doc: ProgrammeUpdatesDocumentsModel) => {
        if (doc.id === null || doc.id === undefined || doc.id === "" || doc.id.length <= 8) {
            return <div>{doc.fileName}</div>;
        }

        if (doc.url && doc.url !== "" && isEvidenceAnImage(doc.url)) {
            return (
                <div className="pdf-link" onClick={() => openEvidenceModal({ id: doc.id, name: doc.fileName, url: doc.url })}>
                    {doc.fileName}
                </div>
            );
        }

        if (doc.urlInline && doc.urlInline !== "" && !isEvidenceAnImage(doc.urlInline)) {
            return (
                <div className="pdf-link" onClick={() => viewModel.OpenFile(doc.urlInline)}>
                    {doc.fileName}
                </div>
            );
        }

        return <></>;
    };
    return (
        <Box mt={2}>
            <Typography style={{ marginBottom: "8px", font: "normal normal 700 12px/16px Segoe UI", letterSpacing: "-0.18px", color: "#191919" }} variant="h5">
                Manual Upload Documents
            </Typography>
            <DarwinSelect
                displayName="Document Type"
                execute={(option: any) => viewModel.setSelectedProgrammeUpdateDocumentType(option)}
                fullWidth={true}
                getOptionLabel={(option: any) => option?.displayName || ""}
                options={viewModel.programmeUpdateDocumentTypes}
                placeholder="Please select"
                value={viewModel.selectedProgrammeUpdateDocumentType}
            />
            {/* Uploader only visible if a type is selected */}
            {viewModel.selectedProgrammeUpdateDocumentType && (
                <Uploader handleSelectFile={(event: React.ChangeEvent<HTMLInputElement>) => viewModel.fileChange(event)} isFormDisabled={isFormDisabled} label="" />
            )}
            <Box className="uploadedFile" mt={2}>
                {viewModel.programmeUpdatesDocuments
                    .filter((doc) => !doc.isDeleted)
                    .map((doc, idx) => (
                        <Box key={idx} className="uploadedFile-box" display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box className="uploadedFile-heading" display="flex" alignItems="center">
                                <IconButton onClick={() => viewModel.DownloadFile(doc.url, doc.fileName)}>
                                    <SystemUpdateTwoToneIcon />
                                </IconButton>
                                <Typography variant="body2" style={{ marginLeft: 8 }}>
                                    {renderFileName(doc)}
                                </Typography>
                                {doc.programmeUpdateDocumentTypeId && (
                                    <Typography variant="caption" style={{ marginLeft: "10px" }}>
                                        {" - "}
                                        {viewModel.programmeUpdateDocumentTypes.find((t) => t.id === doc.programmeUpdateDocumentTypeId)?.displayName}
                                    </Typography>
                                )}
                            </Box>
                            <IconButton onClick={() => viewModel.handleDeleteDocument(doc.id)} disabled={isFormDisabled || !doc.canDeleteDocument}>
                                <DeleteOutlinedIcon />
                            </IconButton>
                        </Box>
                    ))}
            </Box>
            <Box mt={2} display="flex" justifyContent="center">
                {viewModel.hasProgrammeUpdatesDocumentsChanged && !isLoading && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            await viewModel.upsert().then(async (apiResult) => {
                                if (apiResult.wasSuccessful) {
                                    await parentViewModel.getProgrammeProgressUpdates();
                                }
                            });
                        }}
                        disabled={isFormDisabled || isLoading || viewModel.IsLoading}
                    >
                        {isLoading || viewModel.IsLoading ? <CircularProgress size={20} /> : "Save documents"}
                    </Button>
                )}
            </Box>
        </Box>
    );
});
